import { Mark } from "@tiptap/pm/model";
import { BubbleMenu, Editor } from "@tiptap/react";
import { FC, useMemo, useState } from "react";
import EditLinkDialog from "./EditLinkDialog";
import { getLinkType, LinkType } from "@src/lib/utils/files";
import { Button } from "@src/lib/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import { Image, Pencil, Trash2 } from "lucide-react";

type Props = {
  editor: Editor;
};

export const TrainingResourceImageLinkBubbleMenu: FC<Props> = ({ editor }) => {
  const [editLinkOpen, setEditLinkOpen] = useState(false);

  const mark = useMemo(() => {
    const { $from } = editor.state.selection;
    return $from.marks().find((mark: Mark) => mark.type.name === "link");
  }, [editor.state.selection]);

  const isImageLink = mark && getLinkType(mark.attrs.href) === LinkType.IMAGE;

  const handleConvertToImage = () => {
    if (!mark) return;

    const href = mark.attrs.href;

    // Get the text content of the entire link by extending the mark range
    const linkText = editor.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to,
      " ",
    );

    // Replace the entire link with an image by extending the selection to cover the whole link
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setImage({ src: href, alt: linkText || "Image" })
      .run();
  };

  return (
    <BubbleMenu
      tippyOptions={{
        duration: 100,
        interactive: true,
        placement: "top",
      }}
      editor={editor}
      className="flex h-10 items-center gap-0 rounded-full bg-bg-elevated p-1 shadow-out-lg"
      shouldShow={({ editor }) => {
        return !!editor.state.selection.$from
          .marks()
          .find((mark: Mark) => mark.type.name === "link");
      }}
    >
      <TooltipProvider>
        <div className="flex max-w-[200px] items-center gap-2 truncate rounded-lg border bg-white px-3 py-1 text-xs text-gray-11 shadow-sm">
          <a
            href={mark?.attrs.href}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate hover:underline"
          >
            {mark?.attrs.href.replace(/(^\w+:|^)\/\//, "")}
          </a>
        </div>

        {isImageLink && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-md"
                onClick={handleConvertToImage}
              >
                <Image className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Convert to Image</TooltipContent>
          </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-md"
              onClick={() => setEditLinkOpen(true)}
            >
              <Pencil className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Edit Link</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-md"
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange("link")
                  .toggleMark("link")
                  .run();
              }}
            >
              <Trash2 className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Remove Link</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {editLinkOpen && (
        <EditLinkDialog
          open={editLinkOpen}
          onOpenChange={setEditLinkOpen}
          hideTextField
          initialLink={mark?.attrs.href}
          onComplete={(_text, link) => {
            if (!mark) {
              return;
            }
            editor
              .chain()
              .focus()
              .extendMarkRange("link")
              .setLink({ href: link })
              .run();
          }}
        />
      )}
    </BubbleMenu>
  );
};
